import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from './stores/authStore';

// Lazy loaded routes with Vite code splitting
// Authentication routes
const AuthRoutes = {
  Login: () => import('./views/Login.vue'),
  RecoverPassword: () => import('./views/RecoverPassword.vue'),
  ResetPassword: () => import('./views/ResetPassword.vue'),
  EmailValidation: () => import('./views/EmailValidation.vue'),
  Maintenance: () => import('./views/Maintenance.vue'),
};

// Main application routes
const AppRoutes = {
  Home: () => import('./views/Home.vue'),
  Profile: () => import('./views/Profile.vue'),
  Perks: () => import('./views/Perks.vue'),
  Contact: () => import('./views/Contact.vue'),
  Comparator: () => import('./views/Comparator.vue'),
  Mover: () => import('./views/Mover.vue'),
  Payment: () => import('./views/Payment.vue'),
  Booking: () => import('./views/Booking.vue'),
  Procedures: () => import('./views/Procedures.vue'),
  Declaration: () => import('./views/Declaration.vue'),
  Inventory: () => import('./views/Inventory.vue'),
  Addresses: () => import('./views/Addresses.vue'),
  Details: () => import('./views/Details.vue'),
  Invoices: () => import('./views/Invoices.vue'),
  Redirect: () => import('./views/Redirect.vue'),
};

const goToTop = () => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
};

const isNotLogged = (to: RouteLocationNormalized, next: ({ }?) => void) => {
  const auth = useAuthStore();
  if (!auth.isLogged) {
    return next();
  }
  const redirect = to.query.u ? decodeURIComponent(to.query.u as string) : { name: 'home' };
  return next(redirect);
};

const isLogged = (to: RouteLocationNormalized, next: ({ }?) => void) => {
  const auth = useAuthStore();
  if (auth.isLogged || !auth.isInit) return next();
  return next({ name: 'login', query: { u: encodeURIComponent(to.fullPath) } });
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: AppRoutes.Home,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/profile',
      name: 'profile',
      component: AppRoutes.Profile,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/perks',
      name: 'perks',
      component: AppRoutes.Perks,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/contact',
      name: 'contact',
      component: AppRoutes.Contact,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/comparator',
      name: 'comparator',
      component: AppRoutes.Comparator,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/mover/:id',
      name: 'mover',
      component: AppRoutes.Mover,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/payment',
      name: 'payment',
      component: AppRoutes.Payment,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    // {
    //   path: '/comparator/:id/services/',
    //   name: 'services',
    //   component: () => import(/* vite-igonre: "services" */ './views/Services.vue'),
    //   meta: {
    //     layout: 'FullLayout',
    //     authRequired: true,
    //   },
    // },
    {
      path: '/comparator/:id/',
      name: 'booking',
      component: AppRoutes.Booking,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/procedures',
      name: 'procedures',
      component: AppRoutes.Procedures,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/invoices',
      name: 'invoices',
      component: AppRoutes.Invoices,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },    
    // {
    //   path: '/comparator/:id/payment',
    //   name: 'comparator_payment',
    //   component: () => import(/* webpackChunkName: "payment" */ './views/Payment.vue'),
    //   meta: {
    //     layout: 'FullLayout',
    //     authRequired: true,
    //   },
    // },
    {
      path: '/declaration',
      name: 'declaration',
      component: AppRoutes.Declaration,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/inventory',
      name: 'inventory',
      component: AppRoutes.Inventory,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/addresses',
      name: 'addresses',
      component: AppRoutes.Addresses,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/details',
      name: 'details',
      component: AppRoutes.Details,
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/quote',
      name: 'quote',
      component: AppRoutes.Details, // Reuse Details component
      meta: {
        layout: 'FullLayout',
        authRequired: true,
      },
    },
    {
      path: '/redirect',
      name: 'redirect',
      component: AppRoutes.Redirect,
      meta: {
        layout: 'BlankLayout',
        authRequired: true,
      },
    },
    {
      path: '/recoverPassword',
      name: 'recoverPassword',
      component: AuthRoutes.RecoverPassword,
      meta: {
        layout: 'LoginLayout',
      },
    },
    {
      path: '/resetPassword',
      name: 'resetPassword',
      component: AuthRoutes.ResetPassword,
      meta: {
        layout: 'LoginLayout',
      },
    },
    {
      path: '/emailValidation',
      name: 'emailValidation',
      component: AuthRoutes.EmailValidation,
      meta: {
        layout: 'LoginLayout',
      },
    },
    {
      path: '/login',
      name: 'login',
      component: AuthRoutes.Login,
      meta: {
        layout: 'LoginLayout',
      },
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: AuthRoutes.Maintenance,
      meta: {
        layout: 'LoginLayout',
        allowLogged: true,
      },
    },
    {
      path: '/:pathMatch(.*)*',
      component: AppRoutes.Home,
      meta: {
        autoRedirect: true,
      },
    },
  ],
});

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next) => {
  goToTop();
  if (to.meta.allowLogged) {
    return next();
  } else if (to.meta.authRequired) {
    return isLogged(to, next);
  } else if (to.meta.autoRedirect) {
    return next({ name: 'login' });
  } else {
    return isNotLogged(to, next);
  }
});

export default router;
